import { apiFetch } from '@/utils/api-fetch'
import { createSharedComposable } from '@vueuse/core'
import { reactive } from 'vue'
import { useI18n } from 'vue-i18n'

export const useMeta = createSharedComposable(() => {
    /**
     * List of metadata for the app.
     */
    const meta = reactive({} as Metadata)

    /**
     * Translations.
     */
    const { t } = useI18n()

    /**
     * Extra meta dimensions.
     */
    const dimensions = computed(
        () =>
            [
                {
                    id: 'g',
                    name: t('gdp-correlation'),
                    view: 'world',
                    description: t('gdp-correlation-description')
                },
                {
                    id: 'i',
                    name: t('importing-risk'),
                    view: 'country',
                    description: t('importing-risk-description'),
                    labelColor: '#7EB2E2'
                },
                {
                    id: 'i',
                    name: t('importing-risk'),
                    view: 'world',
                    description: t('importing-risk-description'),
                    labelColor: '#7EB2E2'
                }
            ] as (Dimension & { view?: 'world' | 'country' })[]
    )

    /**
     * Set the i18n messages.
     */
    const { setLocaleMessage } = useI18n()

    /**
     * Fetch the metadata.
     */
    async function fetch() {
        const data = await apiFetch<Metadata>('metadata')
        data.labels = Object.assign(localMetaLabels, data.labels)
        setLocaleMessage('en-AU', data.labels)
        Object.assign(meta, data)
    }

    /**
     * Get product by id.
     */
    function findProduct(productId: number) {
        return meta.products.find(({ id }) => id === productId)
    }

    /**
     * Get the list of view specific dimensions.
     */
    function getDimensionsForView(view: string) {
        return unref(dimensions).filter(dimension => dimension.view === view)
    }

    return {
        meta,
        dimensions,
        fetch,
        findProduct,
        getDimensionsForView
    }
})

/**
 * Labels that aren't yet provided by the API.
 */
const localMetaLabels = {
    'gdp-correlation': 'GDP Correlation',
    'gdp-correlation-description': 'GDP Correlation Description',
    'importing-risk': 'Importing Risk',
    'importing-risk-description': 'Importing Risk Description',
    'regional-ranking': 'Regional Ranking',
    'global-ranking': 'Global Ranking',
    'tutorial-toggle-ranking':
        'Toggle between showing global or regional ranking.',
    'filter-by-product': 'Filter by product',
    'tutorial-filter-products':
        'Choose to include/exclude individual products from your selection.',
    'select-country': 'Compare country',
    'no-data-available': 'No data available',
    'tutorial-legend': 'Click on a legend item to filter the displayed values.',
    'import-risk-total':
        '{country} imports {value}/{total} worth of products at risk of modern slavery',
    source: `Source: Walk Free 2023, Global Slavery Index 2022. Available from: {url}`,
    'world-framework-title': 'Explore the Global Slavery Index framework',
    'world-framework-description': '',
    'imports-by': 'Order imports by',
    'imports-by-country': 'Country',
    'imports-by-product': 'Product',
    'tutorial-toggle-imports-by-product':
        'Toggle between showing imports by country or product.'
}
